// Shared bits for the Case progress map (Dean, 14 Oct 2026). Colours follow the portal: SimplyFunded blue for what is done, a vivid red for "now".
export const fmtD = (iso) => (iso ? new Date(String(iso).length > 10 ? iso : iso + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "");
export const fmtDY = (iso) => (iso ? new Date(String(iso).length > 10 ? iso : iso + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "");
export const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");
export const today = () => new Date().toISOString().slice(0, 10);

export const SF_BLUE = "#3B70E0";
export const SF_RED = "#E5243B";

export const BOX = {
  done: "bg-[#3B70E0] text-white border-[#3B70E0]",
  now: "bg-[#E5243B] text-white border-[#E5243B] shadow-md",
  // the step after "now" stays grey and dashed until the arrow's prompt is answered — it must never read as done / active (Dean, item 2)
  next: "bg-slate-50 text-slate-500 border-slate-300 border-dashed",
  held: "bg-white text-slate-400 border-slate-300 border-dashed opacity-70",
  not_reached: "bg-slate-50 text-slate-400 border-slate-200 border-dashed",
  retired: "bg-slate-50 text-slate-300 border-slate-200 border-dotted line-through",
};
export const SUB_BOX = {
  done: "bg-[#3B70E0]/15 text-[#1f4bb3] border-[#3B70E0]/40",
  now: "bg-[#E5243B] text-white border-[#E5243B] shadow",
  next: "bg-white text-slate-500 border-slate-300 border-dashed",
  not_reached: "bg-red-50/60 text-red-300 border-red-200 border-dashed",
};
export const STATUS_CHIP = {
  not_started: "bg-slate-100 text-slate-500 border-slate-200",
  live: "bg-red-50 text-[#c81e32] border-red-200",
  held: "bg-amber-50 text-amber-800 border-amber-300",
  paused: "bg-amber-50 text-amber-800 border-amber-300",
  finished: "bg-emerald-50 text-emerald-800 border-emerald-300",
};
export const STATUS_LABEL = { not_started: "Not started", live: "Live", held: "Held", paused: "Paused", finished: "Finished" };
export const OUTCOME_TONE = { paid: "bg-emerald-600 text-white border-emerald-600", written_off: "bg-slate-500 text-white border-slate-500", dismissed: "bg-red-100 text-red-900 border-red-300", switched: "bg-[#3B70E0]/15 text-[#1f4bb3] border-[#3B70E0]/40" };
export const SLA_DOT = { red: "bg-red-500", amber: "bg-amber-400", green: "bg-emerald-500" };
export const PRIMARY_BTN = "bg-[#E5243B] hover:bg-[#c81e32] text-white";
export const BLUE_BTN = "bg-[#3B70E0] hover:bg-[#2f5cc0] text-white";

export const refsLine = (refs) => Object.entries(refs || {}).filter(([, v]) => v !== "" && v != null).map(([k, v]) => `${k.replace(/_/g, " ")} ${typeof v === "object" ? v.name || "" : v}`).join(" · ");

export const collapseKey = (caseId) => `sf-legal-map-${caseId}`;
export const readCollapse = (caseId) => { try { return JSON.parse(localStorage.getItem(collapseKey(caseId)) || "{}"); } catch { return {}; } };
export const writeCollapse = (caseId, v) => { try { localStorage.setItem(collapseKey(caseId), JSON.stringify(v)); } catch { /* ignore */ } };

export const nowStep = (track) => (track?.steps || []).find((s) => s.state === "now");
export const activeArrow = (track) => (track?.arrows || []).find((a) => a.active);

// capture fields: the designer types them; older published bodies had no type, so infer from the key the same way the backend does
export const captureType = (c) => {
  if (c?.type) return c.type;
  const k = (c?.key || "").toLowerCase();
  if (k === "court") return "court";
  if (["method", "process", "outcome_kind"].includes(k)) return "select";
  if (/(_date|_on|dated)$/.test(k) || ["dated", "served_on", "filed_on", "received_on"].includes(k)) return "date";
  if (["amount", "fee", "deposit", "debt", "costs"].includes(k) || /(_amount|_fee|_total)$/.test(k)) return "amount";
  return "text";
};

// the portal's existing wizards a step can launch from the map (the designer picks which show on each step)
export const WIZARDS = {
  lba: { label: "Letter Before Action", group: "documents", kind: "doc", doc: "lba" },
  dn: { label: "Default Notice", group: "documents", kind: "doc", doc: "dn" },
  pdn: { label: "Pre-Default Notice", group: "documents", kind: "doc", doc: "pdn" },
  sd2: { label: "Statutory Demand (England) — SD2", group: "documents", kind: "doc", doc: "sd2" },
  sd_scot: { label: "Statutory Demand (Scotland)", group: "documents", kind: "doc", doc: "sd_scot" },
  n244_cover: { label: "N244 — substituted service cover", group: "court", kind: "doc", doc: "n244_cover" },
  bp_cover: { label: "Bankruptcy petition cover letter", group: "court", kind: "doc", doc: "bp_cover" },
  iaa_cover: { label: "Form IAA — vacate & withdraw cover", group: "court", kind: "doc", doc: "iaa_cover" },
  n244_adjourn: { label: "N244 — adjournment cover", group: "court", kind: "doc", doc: "n244_adjourn" },
  lpc_instruction: { label: "LPC instruction letter", group: "court", kind: "doc", doc: "lpc_instruction" },
  advocate_sheet: { label: "Advocate instruction sheet", group: "court", kind: "doc", doc: "advocate_sheet" },
  service_record: { label: "Record service (method + certificate)", group: "engine", kind: "service_record" },
  service_pack: { label: "Service pack → process server", group: "engine", kind: "service_pack" },
  review_outcome: { label: "Record review outcome", group: "engine", kind: "review_outcome" },
  petition_hub: { label: "Bankruptcy petition hub", group: "engine", kind: "petition_hub" },
  hearing_outcome: { label: "Record hearing outcome", group: "engine", kind: "hearing_outcome" },
  menzies_pack: { label: "Menzies claim pack", group: "engine", kind: "menzies_pack" },
  court_costs: { label: "Court costs ledger", group: "engine", kind: "court_costs" },
  legal_event: { label: "Add a diary / court date", group: "diary", kind: "legal_event" },
  witness_statement: { label: "Witness statement — draft with AI (PD 32)", group: "court", kind: "witness_statement" },
};
export const wizardVerb = (key, stepName) => {
  const w = WIZARDS[key];
  if (!w) return key;
  if (w.kind === "doc") return `Create ${w.label} now`;
  if (key === "petition_hub") return "Open the petition hub";
  if (key === "legal_event") return "Add a diary / court date";
  if (key === "witness_statement") return "Draft a witness statement";
  return w.label;
};
